import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Ticket, Printer, X } from "lucide-react";

/**
 * Componente per stampare tagliandini ritiro feste/sagre.
 * Mostra modal con scelta modalità: per_item (1 ticket per pezzo) o grouped (1 ticket per riga).
 * Trigger esterno: open/setOpen via parent.
 */
export default function TicketPrint({ orderId, onClose }) {
  const [mode, setMode] = useState("per_item");
  const [tickets, setTickets] = useState(null);
  const [loading, setLoading] = useState(false);

  const generate = async () => {
    setLoading(true);
    try {
      const r = await api.post(`/orders/${orderId}/print-tickets`, { mode });
      setTickets(r.data.tickets);
      toast.success(`${r.data.tickets_count} tagliandini generati`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setLoading(false); }
  };

  const doPrint = () => {
    window.print();
    toast.success("Inviato alla stampante (browser).");
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div className="bg-white rounded-md w-full max-w-2xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-velta-border flex items-center justify-between print:hidden">
          <div>
            <div className="text-xs font-mono uppercase tracking-widest text-velta-mute">Tagliandini ritiro</div>
            <h3 className="font-heading text-2xl font-semibold">Stampa per la festa</h3>
          </div>
          <button onClick={onClose} data-testid="ticket-modal-close"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex-1 overflow-auto p-5 space-y-4 print:p-0">
          {!tickets && (
            <>
              <div className="grid grid-cols-2 gap-3 print:hidden">
                <button
                  data-testid="ticket-mode-per-item"
                  onClick={() => setMode("per_item")}
                  className={`p-4 rounded-md border-2 text-left ${mode === "per_item" ? "border-[#2F9E77] bg-[#2F9E77]/5" : "border-velta-border"}`}
                >
                  <div className="text-2xl mb-1">🎟️ 🎟️ 🎟️</div>
                  <div className="font-semibold">Un tagliandino per pezzo</div>
                  <div className="text-xs text-velta-mute mt-1">Es: 3 bistecche → 3 tagliandini separati. Cliente li distribuisce.</div>
                </button>
                <button
                  data-testid="ticket-mode-grouped"
                  onClick={() => setMode("grouped")}
                  className={`p-4 rounded-md border-2 text-left ${mode === "grouped" ? "border-[#2F9E77] bg-[#2F9E77]/5" : "border-velta-border"}`}
                >
                  <div className="text-2xl mb-1">🎟️×3</div>
                  <div className="font-semibold">Un tagliandino raggruppato</div>
                  <div className="text-xs text-velta-mute mt-1">Es: 3 bistecche → 1 tagliandino con scritto &quot;x3&quot;. Cliente fa una sola coda.</div>
                </button>
              </div>
              <button
                data-testid="ticket-generate"
                onClick={generate}
                disabled={loading}
                className="w-full h-12 bg-[#2F9E77] hover:bg-[#27855F] text-white rounded-md font-semibold btn-tactile disabled:opacity-40 print:hidden"
              >
                {loading ? "Generazione…" : "Genera tagliandini"}
              </button>
            </>
          )}

          {tickets && (
            <>
              <div className="text-xs text-velta-mute mb-2 print:hidden">{tickets.length} tagliandini pronti — anteprima sotto:</div>
              <div className="grid grid-cols-2 gap-3 print:grid-cols-1 print:gap-1">
                {(Array.isArray(tickets) ? tickets : []).map((t, i) => (
                  <div key={i} data-testid={`ticket-${i}`} className="border border-dashed border-velta-ink rounded-md p-4 font-mono text-center bg-velta-bg print:break-inside-avoid print:border-black print:bg-white">
                    <div className="text-[10px] text-velta-mute uppercase tracking-widest">Tagliando ritiro</div>
                    <div className="font-mono font-bold text-2xl tracking-tight mt-1">{t.ticket_number}</div>
                    <div className="border-t border-dashed border-velta-border my-2"></div>
                    <div className="text-xl font-bold">{t.item_name}</div>
                    {t.qty > 1 && <div className="text-3xl font-mono font-bold mt-1">× {t.qty}</div>}
                    {t.note && <div className="text-xs text-velta-mute italic mt-1">{t.note}</div>}
                    <div className="text-[9px] text-velta-mute mt-2">→ {t.print_to === "bar" ? "Bar" : "Cucina"}</div>
                  </div>
                ))}
              </div>
              <button
                data-testid="ticket-print"
                onClick={doPrint}
                className="w-full h-12 bg-velta-ink text-white rounded-md font-semibold btn-tactile inline-flex items-center justify-center gap-2 print:hidden"
              >
                <Printer className="w-4 h-4" /> Stampa
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
